import React, { useCallback, useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import AvaterImage from '../../../components/AvaterImage';
import DashboardHeaderWithFilters from '../../../components/DashboardHeaderWithFilters';

import { GET_REALTOR_DETAILS } from '../../../graphql/admin_queries';
import useCustomQuery from '../../../network/useCustomQuery';
import {
  dateFormatter,
  baseRealtorUrl,
  defaultImageUrl,
} from '../../../utils/helpers';
import useToast from '../../../customHooks/useToast';
import NewcopyIcon from '../../../svgs/NewcopyIcon';
import ReloadIcon from '../../../svgs/ReloadIcon';
import Dots from '../../../components/Loading/Dots';
import Styles from './RealtorReferralDetailsCard.module.scss';

interface IRealtorReferralDetailsCard {
  rewardId: string;
  setReferralId: (id: string) => void;
}

const RealtorReferralDetailsCard = ({
  rewardId,
  setReferralId,
}: IRealtorReferralDetailsCard) => {
  const Toast = useToast();
  const [isCopied, setIsCopied] = useState(false);
  const [getPropertyAgentDetails, loading, data] = useCustomQuery({
    query: GET_REALTOR_DETAILS,
    onError: () => {},
    onData: () => {},
  });

  const fetchPropertyAgentDetails = useCallback(
    () => getPropertyAgentDetails({ variables: { agentId: rewardId } }),
    [rewardId],
  );

  useEffect(() => {
    fetchPropertyAgentDetails();
  }, []);

  useEffect(() => {
    if (data?.getPropertyAgentDetails?.data?.id) {
      setReferralId(rewardId);
    }
  }, [data]);

  const handleCopy = () => {
    setIsCopied(true);
    Toast.success('Code copied to clipboard');

    setTimeout(() => {
      setIsCopied(false);
    }, 5000);
  };

  const details = [
    {
      title: 'Realtor',
      value: `${data?.getPropertyAgentDetails?.data?.firstName} ${data?.getPropertyAgentDetails?.data?.lastName}`,
      col: 'col-xl-3 col-md-6',
    },
    {
      title: 'No. of referrals',
      value: `${data?.getPropertyAgentDetails?.data?.totalReferrals || 0}`,
      col: 'col-xl-2 col-md-6',
    },

    {
      title: 'Date generated',
      value: dateFormatter(
        new Date(Number(data?.getPropertyAgentDetails?.data?.createdAt)),
        'MMM DD, YYYY',
        '**********',
      ),
      col: 'col-xl-3 col-md-6',
    },
  ];

  const activeStatus = data?.getPropertyAgentDetails?.data?.status?.toLowerCase?.() === 'active';
  const referralCode = data?.getPropertyAgentDetails?.data?.code;
  const realtorReferralLink = `${baseRealtorUrl}?referralCode=${referralCode}`;

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.wrapper__avatarAndId}>
        <div className={Styles.wrapper__avatar}>
          <AvaterImage
            image={
              data?.getPropertyAgentDetails?.data?.selfie || defaultImageUrl
            }
            width="40px"
            height="40px"
          />
        </div>
        <div className={Styles.wrapper__IdStatusAndCopy}>
          <div className={Styles.wrapper__Id}>
            {data?.getPropertyAgentDetails?.data?.code}
          </div>
          <div
            className={`${Styles.statusWrapper__status} ${
              activeStatus
                ? Styles['statusWrapper__status--active']
                : Styles['statusWrapper__status--inactive']
            }`}
          >
            <p className={Styles.statusWrapper__statusText}>
              {activeStatus ? 'Active' : 'Inactive'}
            </p>
          </div>

          <div className={Styles.wrapper__copy}>
            <CopyToClipboard text={realtorReferralLink} onCopy={handleCopy}>
              <span>
                {isCopied ? 'Copied' : 'Copy Link'}
                <NewcopyIcon />
              </span>
            </CopyToClipboard>
          </div>
        </div>
      </div>
      <div className={Styles.wrapper__detailsAndAction}>
        <div className={Styles.wrapper__details}>
          <div className={`row ${Styles.wrapper__detailsRow}`}>
            {details?.map((list, index) => (
              <div
                className={`${list?.col} ${Styles.wrapper__detailsCol}`}
                key={index}
              >
                <div className={Styles.wrapper__detailsTitle}>
                  {list?.title}
                </div>
                <div className={Styles.wrapper__detailsValue}>
                  {loading ? (
                    <div className={Styles.wrapper__detailsLoader}>
                      <Dots />
                    </div>
                  ) : (
                    list?.value
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={Styles.wrapper__action}>
          <div className={Styles.wrapper__actionReLoad}>
            {!loading && (
              <span onClick={() => fetchPropertyAgentDetails()}>
                <ReloadIcon />
              </span>
            )}
          </div>
          <div>
            <DashboardHeaderWithFilters />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtorReferralDetailsCard;
